/* eslint-disable no-undef */
/**
 * Event listener for sqv:toggle custom event sent by StoryStream.
 * Opens the PLP-style add-to-bag overlay for products shown via StoryStream.
 */

import { loadContent } from '../util/fetchutils';
import convertSizes from '../components/sizeConversions';

const SELECTORS = {
  STORYSTREAM_PRODUCT: '.stry-stl-product',
  OVERLAY: '.product-tile__add-overlay',
  OVERLAY_HEADER: '.product-tile__add-overlay-header',
  OVERLAY_SIZES: '.product-tile__add-overlay-sizes',
  CLOSE_BUTTON: '.product-tile__add-overlay-close',
  ADD_TO_CART_BUTTON: '.add-to-cart',
  MINICART: '.minicart',
  MINICART_PANE: '.minicart__pane',
  MINICART_CONTAINER: '.minicart__container',
};

const CLASSES = {
  OVERLAY_HOST: 'product-tile__image',
  OVERLAY_HOST_ADD: 'product-tile__image--add-overlay',
  VEILED: 'veiled',
};

const DEFAULT_OVERLAY_HEIGHT = 392;
const EVENT_NAMES = {
  COUNT_UPDATE: 'count:update',
  AFTER_ADD_TO_CART: 'product:afterAddToCart',
};

// Resolves the storefront base path used for cart and overlay requests.
const getBasePath = () => {
  const basePath = window.beesfra && window.beesfra.app && window.beesfra.app.basePath;
  if (!basePath) {
    return null;
  }

  const match = basePath.match(/^(\/on\/demandware\.static\/[^/]+\/[^/]+\/[^/]+)/);
  return match ? match[1].replace('/demandware.static/', '/demandware.store/').replace('/-/', '/') : null;
};

// Finds the StoryStream tile that matches the incoming product ID.
const findProductContainer = (productID) => {
  if (!productID) {
    return undefined;
  }

  const numericValue = String(productID).replace(/\D/g, '');
  const products = document.querySelectorAll(SELECTORS.STORYSTREAM_PRODUCT);

  return Array.from(products).find((product) => {
    const values = [
      product.dataset.pid,
      product.dataset.productId,
      product.dataset.productid,
      product.getAttribute('data-pid'),
      product.getAttribute('data-product-id'),
    ];

    const image = product.querySelector('img');
    const link = product.querySelector('a');

    if (image) {
      values.push(image.getAttribute('src'));
    }

    if (link) {
      values.push(link.getAttribute('href'));
    }

    const combined = values.filter(Boolean).join(' ').toLowerCase();
    return numericValue && combined.includes(numericValue);
  });
};

// Detects the one-size overlay variant and returns the add-to-cart button when present.
const getOneSizeAddToCartButton = (doc) => {
  const onlyChildWrapper = doc.querySelector('.product-tile__add-overlay-size-wrapper--only-child');
  if (!onlyChildWrapper) {
    return null;
  }

  const sizeLabel = onlyChildWrapper.querySelector('.product-tile__add-overlay-size');
  const sizeInput = onlyChildWrapper.querySelector('.product-tile__add-overlay-size-input');
  const labelText = sizeLabel ? sizeLabel.textContent.trim().toLowerCase() : '';
  const inputValue = sizeInput ? String(sizeInput.value).trim().toLowerCase() : '';
  const isOneSize = labelText === 'one' || labelText === 'one size' || inputValue === 'one';

  if (!isOneSize || !sizeInput) {
    return null;
  }

  return onlyChildWrapper.querySelector(SELECTORS.ADD_TO_CART_BUTTON);
};

// Triggers the same cart update hooks used by the standard storefront flow.
const triggerAddToCartEvents = (data) => {
  const jq = window.jQuery || window.$;
  const cartData = data && (data.cart || data.basket || data);
  const countValue = cartData && cartData.numItems !== undefined ? cartData.numItems : (data && data.totalQty) || 0;
  const totalValue = cartData && cartData.totals
    && cartData.totals.grandTotal !== undefined ? cartData.totals.grandTotal : (data && data.grandTotal) || '';

  document.querySelectorAll('.minicart-quantity').forEach((node) => {
    node.textContent = countValue;
    node.dataset.qt = String(countValue);
  });

  document.querySelectorAll('.minicart-grand-total').forEach((node) => {
    node.textContent = totalValue;
    node.dataset.total = String(totalValue);
  });

  if (jq) {
    jq('.minicart').trigger(EVENT_NAMES.COUNT_UPDATE, data);
    jq('.minicart-quantity').trigger(EVENT_NAMES.COUNT_UPDATE, data);
    jq('body').trigger(EVENT_NAMES.AFTER_ADD_TO_CART, data);
    return;
  }

  const miniCart = document.querySelector(SELECTORS.MINICART);
  if (miniCart) {
    miniCart.dispatchEvent(new CustomEvent(EVENT_NAMES.COUNT_UPDATE, { bubbles: true, detail: data }));
  }

  document.body.dispatchEvent(new CustomEvent(EVENT_NAMES.AFTER_ADD_TO_CART, { bubbles: true, detail: data }));
};

const getMiniCartElements = () => {
  const miniCart = document.querySelector(SELECTORS.MINICART);
  const pane = document.querySelector(SELECTORS.MINICART_PANE);
  const content = pane ? pane.querySelector(SELECTORS.MINICART_CONTAINER) : null;

  return { miniCart, pane, content };
};

// Restores the tile host after the overlay is closed.
const resetOverlayHost = (overlayHost) => {
  if (!overlayHost) {
    return;
  }

  overlayHost.classList.remove(CLASSES.OVERLAY_HOST_ADD, CLASSES.OVERLAY_HOST, CLASSES.VEILED);
  overlayHost.style.cssText = '';
};

// Applies the positioning styles needed to keep the overlay anchored to the tile image.
const applyOverlayStyles = (overlayHost, overlayElement) => {
  if (!overlayElement) {
    return;
  }

  const image = overlayHost.querySelector(SELECTORS.IMAGE);
  const overlayHeight = Math.min(image ? image.offsetHeight : DEFAULT_OVERLAY_HEIGHT, DEFAULT_OVERLAY_HEIGHT);

  overlayElement.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: ${overlayHeight}px;
  `;
};

// Loads the standard minicart content into the flyout pane after a successful add.
const openMiniCartFlyout = async () => {
  const { miniCart, pane, content } = getMiniCartElements();

  if (!miniCart || !pane || !content) {
    return;
  }

  const actionUrl = miniCart.dataset.actionUrl || miniCart.getAttribute('data-action-url');
  if (!actionUrl) {
    return;
  }

  pane.classList.add('show');
  content.innerHTML = '';

  try {
    const response = await fetch(actionUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    const html = await response.text();
    content.innerHTML = html;
    pane.classList.add('show');

    const body = document.body;
    if (body) {
      body.style.borderRightWidth = '17px';
      body.classList.add('custom-minicart-open');
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load minicart flyout:', error);
  }
};

// Sends the add-to-cart request and opens the flyout on success.
const addToCart = async (pid, quantity = 1) => {
  const basePath = getBasePath();
  if (!basePath || !pid) {
    return undefined;
  }

  const addToCartUrl = `${basePath}/Cart-AddProduct`;
  const formData = new URLSearchParams();
  formData.set('pid', pid);
  formData.set('quantity', String(quantity));

  try {
    const response = await fetch(addToCartUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });

    const data = await response.json().catch(() => ({}));
    if (!data.error) {
      triggerAddToCartEvents(data);
      await openMiniCartFlyout();
    }

    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to add to cart:', error);
    return { error: true };
  }
};

// Binds the overlay close control to the tile host.
const bindOverlayEvents = (overlayHost, overlayElement) => {
  const closeButton = overlayElement.querySelector('.product-tile__add-overlay-close');
  if (closeButton) {
    closeButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const currentOverlay = overlayHost.querySelector(SELECTORS.OVERLAY);
      if (currentOverlay) {
        currentOverlay.remove();
      }

      resetOverlayHost(overlayHost);
    });
  }
};

// Loads the overlay markup and decides whether to show the overlay or bypass it for one-size products.
const displayOverlay = async (productID) => {
  const targetContainer = findProductContainer(productID);

  if (!targetContainer) {
    return;
  }

  const basePath = getBasePath();
  if (!basePath) {
    return;
  }

  try {
    const tempElement = document.createElement('p');
    tempElement.className = 'product-tile__action-add';
    tempElement.setAttribute('data-action', `${basePath}/Tile-AddToBagOverlay?pid=${encodeURIComponent(productID)}`);

    const { content } = await loadContent(tempElement);
    if (!content) {
      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const addButton = getOneSizeAddToCartButton(doc);

    if (addButton) {
      const pid = addButton.dataset.pid;
      const minQuantity = parseInt(addButton.dataset.minQuantity || '1', 10);
      if (pid) {
        await addToCart(pid, minQuantity);
        return;
      }
    }

    const overlayHost = targetContainer.querySelector(`.${CLASSES.OVERLAY_HOST}`) || targetContainer;
    const existingOverlay = overlayHost.querySelector(SELECTORS.OVERLAY);
    if (existingOverlay) {
      existingOverlay.remove();
    }

    overlayHost.insertAdjacentHTML('beforeend', content);
    overlayHost.classList.add(CLASSES.OVERLAY_HOST_ADD, CLASSES.OVERLAY_HOST, CLASSES.VEILED);
    overlayHost.style.cssText = 'aspect-ratio: unset; background-color: transparent; overflow: visible;';

    const overlayElement = overlayHost.querySelector(SELECTORS.OVERLAY);
    if (overlayElement) {
      applyOverlayStyles(overlayHost, overlayElement);
      bindOverlayEvents(overlayHost, overlayElement);
      convertSizes();
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[sqvOverlay] failed to display overlay', error);
  }
};

// Registers the StoryStream toggle listener.
export default () => {
  window.addEventListener('sqv:toggle', (event) => {
    if (event && event.detail && event.detail.productID) {
      displayOverlay(event.detail.productID);
    }
  });
};
